import { useCallback, useEffect, useSyncExternalStore } from "react";
import { fetchUserProfile } from "./profileApi";
import { fetchMarketplaceShop } from "./marketplaceApi";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import { boardAgGridThemeForClass } from "./boardAgGridThemes";
import {
  getCosmeticPreview,
  subscribeCosmeticPreview,
} from "./cosmeticPreviewStore";

export const DARK_CONTENT_THEME_CLASSES = new Set([
  "pb-board-theme-twilight-deck",
  "pb-board-theme-copper-hearth",
  "pb-board-theme-emerald-vault",
  "pb-board-theme-obsidian-gold",
  "pb-board-theme-aurora-fjord",
  "pb-board-theme-pixel-arcade",
  "pb-board-theme-neon-district",
  "pb-board-theme-solar-flare",
  "pb-board-theme-void-orchid",
  "pb-board-theme-glacier-pulse",
]);

const EMPTY = {
  themeClass: "",
  isLegendary: false,
  agGridTheme: boardAgGridThemeForClass(""),
  isPreview: false,
};

/** Equipped theme from profile (persisted). Preview overrides this in getBoardThemeSnapshot. */
let equippedSnapshot = { ...EMPTY };
const listeners = new Set();

function snapshotFromThemeClass(themeClass, { isPreview = false } = {}) {
  if (!themeClass) {
    return { ...EMPTY, isPreview };
  }
  return {
    themeClass,
    isLegendary: DARK_CONTENT_THEME_CLASSES.has(themeClass),
    agGridTheme: boardAgGridThemeForClass(themeClass),
    isPreview,
  };
}

function computeDisplaySnapshot() {
  const preview = getCosmeticPreview()?.item;
  if (preview?.type === "boardTheme" && preview.themeClass) {
    return snapshotFromThemeClass(preview.themeClass, { isPreview: true });
  }
  return { ...equippedSnapshot, isPreview: false };
}

let displaySnapshot = computeDisplaySnapshot();

function emit() {
  displaySnapshot = computeDisplaySnapshot();
  for (const fn of listeners) {
    fn();
  }
}

function setEquippedSnapshot(next) {
  equippedSnapshot = { ...next, isPreview: false };
  emit();
}

export function getBoardThemeSnapshot() {
  return displaySnapshot;
}

export function subscribeBoardTheme(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useBoardTheme() {
  return useSyncExternalStore(subscribeBoardTheme, getBoardThemeSnapshot, getBoardThemeSnapshot);
}

/** @deprecated Prefer useBoardTheme().themeClass */
export function useEquippedBoardTheme() {
  return useBoardTheme().themeClass;
}

export function useBoardThemeLoader() {
  const marketplaceOn = isPluginEnabled(PLUGIN_IDS.MARKETPLACE);

  const resolveTheme = useCallback(async () => {
    if (!marketplaceOn) {
      setEquippedSnapshot({ ...EMPTY });
      return;
    }
    try {
      const [profile, shop] = await Promise.all([
        fetchUserProfile(),
        fetchMarketplaceShop().catch(() => null),
      ]);
      const themeId = profile?.equippedBoardThemeId || null;
      if (!themeId) {
        setEquippedSnapshot({ ...EMPTY });
        return;
      }
      const owned = new Set(
        Array.isArray(profile.marketplaceOwned) ? profile.marketplaceOwned : shop?.owned || []
      );
      if (!owned.has(themeId)) {
        setEquippedSnapshot({ ...EMPTY });
        return;
      }
      const catalog = Array.isArray(shop?.catalog) ? shop.catalog : [];
      const item = catalog.find((i) => i.id === themeId);
      const themeClass =
        item?.type === "boardTheme" && item.themeClass ? item.themeClass : "";
      if (!themeClass) {
        setEquippedSnapshot({ ...EMPTY });
        return;
      }
      setEquippedSnapshot(snapshotFromThemeClass(themeClass));
    } catch {
      setEquippedSnapshot({ ...EMPTY });
    }
  }, [marketplaceOn]);

  useEffect(() => {
    resolveTheme();
    const onProfile = () => resolveTheme();
    const onImpersonation = () => resolveTheme();
    const unsubPreview = subscribeCosmeticPreview(() => emit());
    window.addEventListener("pb-user-profile-changed", onProfile);
    window.addEventListener("pb-impersonation-changed", onImpersonation);
    return () => {
      unsubPreview();
      window.removeEventListener("pb-user-profile-changed", onProfile);
      window.removeEventListener("pb-impersonation-changed", onImpersonation);
    };
  }, [resolveTheme]);
}
